import { SMARTDATA_PROCESSING_OPTIONS } from 'js/utils/constant.js';
import { mapActions, mapState } from 'pinia';
import { useSmartviewStore } from 'store/pinia/useSmartviewStore';

const color = 'blue-grey lighten-2';
const DEFAULT_PROCESSING_MODE_AVAILABILITY = Object.freeze({
    inspection_view: Object.freeze({
        processBySmartData: true,
        uploadPreprocessed: true,
    }),
    ortho_view: Object.freeze({
        processBySmartData: true,
        uploadPreprocessed: true,
    }),
    cesium_tiles_view: Object.freeze({
        processBySmartData: true,
        uploadPreprocessed: true,
    }),
    bim_view: Object.freeze({
        processBySmartData: true,
        uploadPreprocessed: false,
    }),
    point_cloud_view: Object.freeze({
        processBySmartData: true,
        uploadPreprocessed: true,
    }),
    gaussian_splat_view: Object.freeze({
        processBySmartData: true,
        uploadPreprocessed: true,
    }),
    dtm_view: Object.freeze({
        processBySmartData: false,
        uploadPreprocessed: false,
    }),
    thermal_inspection_view: Object.freeze({
        processBySmartData: true,
        uploadPreprocessed: false,
    }),
});

export function getPlanUploadPreprocessedViewTypes(teamPlan = {}) {
    const configuredViewTypes = teamPlan?.upload_preprocessed_view_types;
    if (Array.isArray(configuredViewTypes)) {
        return configuredViewTypes.filter(Boolean);
    }

    if (configuredViewTypes) {
        try {
            const parsedViewTypes = JSON.parse(configuredViewTypes);
            if (Array.isArray(parsedViewTypes)) {
                return parsedViewTypes.filter(Boolean);
            }
        } catch (error) {
            return String(configuredViewTypes)
                .split(',')
                .map((viewType) => viewType.trim())
                .filter(Boolean);
        }
    }

    return [];
}

export function isViewAvailableForProcessingOption(
    view = {},
    processingOption,
    teamPlan = {}
) {
    if (!view || view.secondary || view.hiddenFromUploadPage) {
        return false;
    }

    if (!processingOption) {
        return true;
    }

    const modeConfig =
        view.availableOnProcessingModes ||
        DEFAULT_PROCESSING_MODE_AVAILABILITY[view.type] ||
        {};

    if (
        processingOption ===
        SMARTDATA_PROCESSING_OPTIONS.UPLOAD_PREPROCESSED_DATA
    ) {
        const planOverrideViewTypes = getPlanUploadPreprocessedViewTypes(teamPlan);
        if (planOverrideViewTypes.includes(view.type)) {
            return true;
        }

        return modeConfig.uploadPreprocessed !== false;
    }

    if (
        processingOption ===
        SMARTDATA_PROCESSING_OPTIONS.PROCESS_BY_SMARTDATA
    ) {
        return modeConfig.processBySmartData !== false;
    }

    return true;
}

export default {
    data() {
        return {
            view_options: {
                inspection_view: {
                    // the name of the view from the database
                    text: 'Inspection', // the name of the view displayed to users
                    value: `inspection`,
                    color, // the color for the date and views menus
                    icon: '$vuetify.icons.inspectionView', // the icon for the time and views menus
                    type: 'inspection_view', // the name of the view from the database again
                    downloadDescription: 'The original RGB images.',
                    disabled: false,
                    availableOnProcessingModes: {
                        processBySmartData: true,
                        uploadPreprocessed: true,
                    },
                },
                ortho_view: {
                    text: '2D Map',
                    value: '2d',
                    color,
                    icon: '$vuetify.icons.orthoView',
                    type: 'ortho_view',
                    downloadDescription: 'A GeoTIFF of your 2D map.',
                    disabled: false,
                    availableOnProcessingModes: {
                        processBySmartData: true,
                        uploadPreprocessed: true,
                    },
                },
                cesium_tiles_view: {
                    text: '3D Model',
                    value: '3d',
                    color,
                    icon: '$vuetify.icons.cesiumTilesView',
                    type: 'cesium_tiles_view',
                    downloadDescription: 'Your 3D model as a .obj.',
                    disabled: false,
                    availableOnProcessingModes: {
                        processBySmartData: true,
                        uploadPreprocessed: true,
                    },
                },
                bim_view: {
                    text: 'BIM Model',
                    value: 'bim',
                    color,
                    icon: '$vuetify.icons.bimView',
                    type: 'bim_view',
                    downloadDescription: 'Your original ifc/dtm files.',
                    disabled: true,
                    availableOnProcessingModes: {
                        processBySmartData: true,
                        uploadPreprocessed: false,
                    },
                },
                point_cloud_view: {
                    text: 'Point Cloud',
                    value: 'point_cloud',
                    color,
                    icon: '$vuetify.icons.pointCloudView',
                    type: 'point_cloud_view',
                    downloadDescription:
                        'The LAS/LAZ files of your asset. If the individual files are unavailable, download the full folder instead.',
                    disabled: false,
                    availableOnProcessingModes: {
                        processBySmartData: true,
                        uploadPreprocessed: true,
                    },
                },
                gaussian_splat_view: {
                    text: 'Gaussian Splat',
                    value: 'gaussian_splat',
                    color,
                    icon: 'mdi-scatter-plot',
                    type: 'gaussian_splat_view',
                    downloadDescription: 'Your Gaussian Splat viewer package.',
                    disabled: false,
                    availableOnProcessingModes: {
                        processBySmartData: true,
                        uploadPreprocessed: true,
                    },
                },
                dtm_view: {
                    text: 'Terrain - DTM',
                    value: 'terrain_dtm',
                    color,
                    icon: '$vuetify.icons.terrainView',
                    type: 'dtm_view',
                    hiddenFromUploadPage: true,
                    downloadDescription: 'Terrain data in a .tiff format.',
                    disabled: false,
                    availableOnProcessingModes: {
                        processBySmartData: false,
                        uploadPreprocessed: false,
                    },
                },
                dsm_view: {
                    text: 'Terrain - DSM',
                    value: 'terrain_dsm',
                    color,
                    icon: '$vuetify.icons.terrainView',
                    type: 'dsm_view',
                    hiddenFromUploadPage: true,
                    downloadDescription: 'Surface elevation data in GeoTIFF format.',
                    disabled: false,
                    availableOnProcessingModes: {
                        processBySmartData: false,
                        uploadPreprocessed: false,
                    },
                },
                thermal_inspection_view: {
                    text: 'Thermal Inspection',
                    value: 'thermal_inspection',
                    color,
                    icon: '$vuetify.icons.thermometer', // the icon for the time and views menus
                    type: 'thermal_inspection_view',
                    hiddenFromUploadPage: true,
                    downloadDescription: 'The original thermal images.',
                    disabled: false,
                    availableOnProcessingModes: {
                        processBySmartData: true,
                        uploadPreprocessed: false,
                    },
                }
            }
        };
    },
    computed: {
        ...mapState(useSmartviewStore, ['getIdCounter'])
    },
    methods: {
        ...mapActions(useSmartviewStore, ['incrementIdCounter']),
        isViewAvailableForProcessingOption(view, processingOption, teamPlan) {
            return isViewAvailableForProcessingOption(
                view,
                processingOption,
                teamPlan
            );
        },
        createNewView(uuid, component = null) {
            return {
                component: component || 'Loading',
                datasetPackage: null,
                viewUUID: uuid,
                style: null,
                class: null,
                id: this.getId(),
                urlParam: null,
                state: 'Loading',
                stateProps: {},
                enableReports: !(
                    component === 'inspection_view' ||
                    component === 'thermal_inspection_view'
                )
            };
        },
        getId() {
            const id = this.getIdCounter;
            this.incrementIdCounter();
            return id;
        }
    }
};
